import * as React from 'react';
import * as _ from 'lodash';
import * as PropTypes from 'prop-types';
import * as classNames from 'classnames';
import {withStyles, WithStyles, createStyles} from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {browserHistory} from 'react-router';
import IconButton from '@material-ui/core/IconButton';
import {Scrollbars} from '../../shared/vendor/react-custom-scrollbars/src/index';
import {TweenLite, Ease} from 'gsap';
import Grid, {GridProps} from '@material-ui/core/Grid';
import {Theme} from '@material-ui/core/styles';
import TextBubble, {TextBubbleProps} from '../../shared/components/TextBubble';
import ActionsPanel from './ActionsPanel';
import {Breakpoint, keys} from '@material-ui/core/styles/createBreakpoints';
import {stringToDashed} from 'cl-automation-utils';
import TooltipIcon from '@material-ui/core/TooltipIcon/TooltipIcon';

const DEFAULT_INTERCOM_PADDING = 20;
const FIXED_INTERCOM_WITH_BOTTOM_ACTIONS_PADDING = 84;

interface BasicPageProps {
  component?: React.ElementType<any>;
  backButton?: boolean;
  scrollTo?: {y: number};
  initialSelectedSubPageIndex?: number;
  selectedSubPageIndex?: number;
  onChange?: (selectedSubPage: number) => void;
}

interface BasicPageState {
  selectedSubPageIndex: number;
  scrollElementRef: React.Component;
}

export interface BasicPageContextTypes {
  getScrollableContainer: () => {};
}

class BasicPage extends React.PureComponent<BasicPageProps & WithStyles<typeof styles>, BasicPageState> {
  static defaultProps: Partial<BasicPageProps> = {
    initialSelectedSubPageIndex: 0,
  };

  startAnimationTimeout = null;

  constructor(props) {
    super(props);
    this.state = {
      selectedSubPageIndex: 0,
      scrollElementRef: null,
    };
    const selectedSubPage = this.getSelectedSubPage();
    const {
      actions: {bottomLeft, bottomRight},
    } = selectedSubPage.props;
    if (bottomLeft || bottomRight) {
      _.defer(BasicPage.padIntercom, FIXED_INTERCOM_WITH_BOTTOM_ACTIONS_PADDING);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {scrollTo} = this.props;

    if (
      this.props.selectedSubPageIndex !== prevProps.selectedSubPageIndex ||
      this.state.selectedSubPageIndex !== prevState.selectedSubPageIndex
    ) {
      const selectedSubPage = this.getSelectedSubPage();
      const {
        actions: {bottomLeft, bottomRight},
      } = selectedSubPage.props;
      if (bottomLeft || bottomRight) {
        BasicPage.padIntercom(FIXED_INTERCOM_WITH_BOTTOM_ACTIONS_PADDING);
      } else {
        BasicPage.padIntercom(DEFAULT_INTERCOM_PADDING);
      }
    }
    if (scrollTo && prevProps.scrollTo !== scrollTo) {
      // noinspection TypeScriptValidateJSTypes
      this.startAnimation(() =>
        // @ts-ignore
        TweenLite.to(this.state.scrollElementRef, 0.25, {scrollTo, ease: Ease.easeInOut})
      );
    }
  }

  componentWillUnmount() {
    BasicPage.padIntercom(DEFAULT_INTERCOM_PADDING);
  }

  static padIntercom(pad) {
    (window as any).Intercom('update', {vertical_padding: pad});
  }

  static childContextTypes = {
    getScrollableContainer: PropTypes.func,
  };

  getSubPageByIndex = (index: number): SubPageType => {
    const {children} = this.props;
    const childrenArray = React.Children.toArray(children);
    return childrenArray[index] as SubPageType;
  };

  isControlledTabs = () => {
    const {selectedSubPageIndex} = this.props;
    return _.isFinite(selectedSubPageIndex);
  };

  getSelectedSubPageIndex = (): number => {
    return this.isControlledTabs() ? this.props.selectedSubPageIndex : this.state.selectedSubPageIndex;
  };

  getSelectedSubPage = (): SubPageType => {
    const selectedSubPageIndex = this.getSelectedSubPageIndex();
    return this.getSubPageByIndex(selectedSubPageIndex);
  };

  startAnimation = animationFunc => {
    if (this.startAnimationTimeout) {
      window.clearTimeout(this.startAnimationTimeout);
    }
    this.startAnimationTimeout = window.setTimeout(() => {
      this.startAnimationTimeout = null;
      animationFunc();
    }, 10);
  };

  changeSelectedSubPage = (event, value) => {
    const {onChange} = this.props;
    if (!this.isControlledTabs()) {
      this.setState({selectedSubPageIndex: value});
    }
    if (onChange) {
      onChange(value);
    }
  };

  getChildContext = (): BasicPageContextTypes => {
    return {
      getScrollableContainer: () => {
        return this.state.scrollElementRef;
      },
    };
  };

  renderTitle = () => {
    const {
      backButton,
      children,
      classes: {title, titleTooltip, titleContainer, tab, tabIcon, bubble},
    } = this.props;
    if (!title) {
      return null;
    }
    const selectedSubPageIndex = this.getSelectedSubPageIndex();
    const childrenArray = React.Children.toArray(children);
    const singleSubPage = childrenArray.length === 1;
    const firstChild = childrenArray[0] as SubPageType;
    return (
      <Paper elevation={3} className={titleContainer}>
        {backButton ? (
          <IconButton id="cla-basicpage-button-back" onClick={browserHistory.goBack}>
            <ArrowBackIcon />
          </IconButton>
        ) : (
          undefined
        )}
        {singleSubPage ? (
          <Typography variant="h6" className={title} id="cla-basicpage-title">
            {firstChild.props.title}
            {firstChild.props.titleTooltip ? (
              <TooltipIcon variant="inline" className={titleTooltip} title={firstChild.props.titleTooltip} />
            ) : (
              undefined
            )}
            {firstChild.props.bubbleProps ? (
              <TextBubble className={bubble} {...firstChild.props.bubbleProps} />
            ) : (
              undefined
            )}
          </Typography>
        ) : (
          <Tabs
            value={selectedSubPageIndex}
            indicatorColor="primary"
            textColor="primary"
            onChange={this.changeSelectedSubPage}
          >
            {childrenArray.map((child: any, index) => {
              const selectedSubPage = selectedSubPageIndex === index;
              const iconProps: {className: string; color?: string} = {className: tabIcon};
              if (selectedSubPage) {
                iconProps.color = 'primary';
              }
              const label = (
                <div className={tab}>
                  {child.props.icon ? React.cloneElement(child.props.icon, iconProps) : undefined}
                  {child.props.title}
                  {child.props.bubbleProps ? (
                    <TextBubble
                      className={bubble}
                      color={selectedSubPage ? 'primary' : undefined}
                      {...child.props.bubbleProps}
                    />
                  ) : (
                    undefined
                  )}
                </div>
              );
              return (
                <Tab id={`cla-tab-${stringToDashed(child.props.title)}`} key={`sub-page-${index}-tab`} label={label} />
              );
            })}
          </Tabs>
        )}
      </Paper>
    );
  };

  handleScrollbarRef = ref => {
    this.setState({scrollElementRef: _.get(ref, 'refs.view')});
  };

  renderBottomActions = () => {
    const {
      actions: {bottomLeft, bottomRight},
      bottomText,
    } = this.getSelectedSubPage().props;
    return bottomLeft || bottomRight ? (
      <React.Fragment>
        <ActionsPanel actions={{left: bottomLeft, right: bottomRight}} text={bottomText} />
        <style
          dangerouslySetInnerHTML={{
            __html: `<!---->#intercom-container .intercom-launcher-frame {bottom: 84px!important}<!---->`,
          }}
        />
      </React.Fragment>
    ) : (
      undefined
    );
  };

  render() {
    const {classes} = this.props;
    const {
      gridContainer,
      root,
      content,
      paddedContent,
      overflowHidden,
      topActionsPanel,
      contentDefault,
      contentWhite,
    } = classes;
    const {
      children,
      contentWidth,
      GridProps,
      noScroll,
      color,
      actions: {topLeft, topRight, selectedLeft, selectedRight},
    } = this.getSelectedSubPage().props;
    let Container = noScroll ? 'div' : Scrollbars;
    return (
      <div className={root}>
        {this.renderTitle()}
        <ActionsPanel
          actions={{left: topLeft, right: topRight, selectedLeft: selectedLeft, selectedRight: selectedRight}}
          className={topActionsPanel}
        />
        {children ? (
          <Container
            className={classNames(content, {
              [paddedContent]: contentWidth !== 'full',
              [contentWhite]: color === 'white',
              [contentDefault]: color === 'default',
            })}
            ref={this.handleScrollbarRef}
          >
            <Grid
              container
              spacing={contentWidth === 'full' ? 0 : 3}
              direction="column"
              alignItems={keys.indexOf(contentWidth as Breakpoint) !== -1 ? 'stretch' : 'center'}
              className={classNames(
                gridContainer,
                {
                  [overflowHidden]: noScroll,
                },
                classes[`${contentWidth}ContentGridContainer`]
              )}
              {...GridProps}
            >
              {children}
            </Grid>
          </Container>
        ) : (
          undefined
        )}
        {this.renderBottomActions()}
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    titleContainer: {
      height: theme.spacing(6),
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      backgroundColor: theme.palette.grey[50],
      zIndex: theme.zIndex.appBar,
      flexShrink: 0,
    },
    title: {
      marginLeft: theme.spacing(1),
    },
    titleTooltip: {
      marginLeft: theme.spacing(1),
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    tabIcon: {
      marginRight: theme.spacing(1),
    },
    bubble: {
      marginLeft: theme.spacing(1),
    },
    content: {
      flexGrow: 1,
      display: 'flex',
      alignContent: 'stretch',
      flexDirection: 'column',
      position: 'relative',
      alignItems: 'center',
    },
    contentDefault: {
      backgroundColor: theme.palette.grey[200],
    },
    contentWhite: {
      backgroundColor: theme.palette.common.white,
    },
    gridContainer: {
      flexShrink: 0,
      flexGrow: 1,
      flexWrap: 'nowrap',
    },
    paddedContent: {
      padding: theme.spacing(3),
    },
    ..._.mapValues(
      _.mapKeys(theme.breakpoints.values, (value, breakpoint) => `${breakpoint}ContentGridContainer`),
      maxWidth => ({maxWidth})
    ),
    overflowHidden: {
      overflow: 'hidden',
    },
    topActionsPanel: {
      backgroundColor: theme.palette.grey[50],
      zIndex: theme.zIndex.appBar - 1,
    },
  });
export default withStyles(styles)(BasicPage);

export interface SubPageProps {
  title: string;
  titleTooltip?: string;
  icon?: React.ReactElement<any>;
  actions?: {
    topLeft?: React.ReactElement<any>;
    topRight?: React.ReactElement<any>;
    bottomLeft?: React.ReactElement<any>;
    bottomRight?: React.ReactElement<any>;
    selectedLeft?: React.ReactElement<any>;
    selectedRight?: React.ReactElement<any>;
  };
  bottomText?: string;
  contentWidth?: 'default' | 'full' | Breakpoint;
  GridProps?: GridProps;
  noScroll?: boolean;
  bubbleProps?: TextBubbleProps;
  color?: 'default' | 'white';
}

type SubPageType = React.ReactElement<SubPageProps & {children: React.ReactChild}>;

export const SubPage: React.FunctionComponent<SubPageProps> = () => {
  return <React.Fragment>{this.props.children}</React.Fragment>;
};
SubPage.defaultProps = {
  actions: {},
  color: 'default',
};
